import React from "react";
import styled from "styled-components";

export interface DropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface DropdownProps {
  options: DropdownOption[];
  onSelect: (value: string) => void;
  toggleComponent: React.ReactNode;
}

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightgrey};
  padding: 0;
  margin: 0;
  list-style: none;
  min-width: 100%;
  z-index: 1;
  font-size: ${({ theme }) => theme.fontSizes.regular};
  color: ${({ theme }) => theme.colors.textDarkBlue};
  background-color: ${({ theme }) => theme.colors.white};
  text-transform: none;
  font-weight: normal;
  outline: none;
`;

const DropdownListItem = styled.li`
  cursor: pointer;
  padding: 0.5em;
  display: flex;
  align-items: center;
  &:hover {
    background-color: ${({ theme }) => theme.colors.lightgrey3};
  }
`;

const DropdownIcon = styled.span`
  display: flex;
  margin-right: 8px;
`;

const DropDown: React.FC<DropdownProps> = ({ options, onSelect, toggleComponent }) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      {React.cloneElement(toggleComponent as React.ReactElement, { onClick: () => setIsOpen(!isOpen) })}
      {isOpen && (
        <DropdownList>
          {options.map(option => (
            <DropdownListItem key={option.value} onClick={handleSelect.bind(null, option.value)}>
              {option.icon && <DropdownIcon>{option.icon}</DropdownIcon>}
              {option.label}
            </DropdownListItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default DropDown;
